import { useState } from 'react';

import type { CreateTimeEntryInput, PlainDate, TimeEntryDto } from '@botguide/shared';
import {
  MINUTES_MAX,
  MINUTES_MIN,
  MINUTES_STEP,
  addDays,
  createTimeEntrySchema,
  stepMinutes,
  todayInBusinessZone,
} from '@botguide/shared';

import { Button, CONTROL_CLASS, ErrorNote, Field, FieldShell, Textarea } from '@/components';
import { cn } from '@/lib/cn';
import { formatHours } from '@/lib/format';

import { TaskPicker } from './TaskPicker';

/*
 * Logging time against a task.
 *
 * The three shapes of a 400 from `POST /api/time-entries` are all designed out
 * of this form rather than reported back from it:
 *
 *  - minutes that are not a quarter-hour step, or outside 15..960: the buttons
 *    step along the grid;
 *  - a work date outside the week being viewed: the date input is bounded;
 *  - a task id that does not exist: the task is picked from a list.
 *
 * **The minutes field is typeable**, so the first of those is designed out
 * only as far as the buttons go. This comment used to claim the control was
 * "a stepper, not a free text box", and that claim was how F26 hid: the
 * buttons added fifteen to whatever was typed, so from 70 they produced 85 and
 * 100 and never a saveable number. The grid, the bounds and the stepping now
 * live in `hours.ts` beside the schema that enforces them.
 *
 * `createTimeEntrySchema` still validates the whole thing before submit and
 * remains the authority for the *rule* - if a control ever disagrees, the
 * parse fails and the person sees the schema's own message.
 */

export interface TimeEntryFormProps {
  /** Whose tasks to offer first. Hours are always logged against the caller. */
  userId: string;
  /** The Monday of the week being logged. Bounds the date picker. */
  weekStart: PlainDate;
  /** Present when editing. Only ever a `pending` entry - the API refuses the rest. */
  entry?: TimeEntryDto;
  submitLabel?: string;
  busy?: boolean;
  /** Whatever the mutation threw, rendered inline above the fields. */
  error?: unknown;
  onSubmit: (values: CreateTimeEntryInput) => void;
  onCancel?: () => void;
}

export function TimeEntryForm({
  userId,
  weekStart,
  entry,
  submitLabel = 'Log time',
  busy = false,
  error,
  onSubmit,
  onCancel,
}: TimeEntryFormProps) {
  const weekEnd = addDays(weekStart, 6);

  const [taskId, setTaskId] = useState(entry?.taskId ?? '');
  const [workDate, setWorkDate] = useState<PlainDate>(entry?.workDate ?? defaultWorkDate(weekStart));
  const [minutesText, setMinutesText] = useState(String(entry?.minutes ?? 60));
  const [notes, setNotes] = useState(entry?.notes ?? '');
  const [issues, setIssues] = useState<Record<string, string>>({});

  /*
   * Clearing an issue removes the key rather than blanking it. `FieldShell`
   * shows `error ?? hint`, and an empty string is not nullish - blanking would
   * swallow the hint instead of restoring it.
   */
  const clearIssue = (key: string) =>
    setIssues((current) => {
      if (current[key] === undefined) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });

  const minutes = Number.parseInt(minutesText, 10);
  const step = (direction: 1 | -1) => {
    setMinutesText(String(stepMinutes(Number.isNaN(minutes) ? null : minutes, direction)));
    clearIssue('minutes');
  };

  function submit() {
    const parsed = createTimeEntrySchema.safeParse({
      taskId,
      workDate,
      minutes: Number.isNaN(minutes) ? undefined : minutes,
      notes,
    });

    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        next[key] ??= issue.message;
      }
      // A missing task id reads as "Invalid uuid" from Zod, which is true and
      // useless. The person did not type a uuid; they failed to pick a task.
      if (!taskId) next.taskId = 'Choose the task these hours belong to.';
      setIssues(next);
      return;
    }

    setIssues({});
    onSubmit(parsed.data);
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      {error ? <ErrorNote error={error} /> : null}

      <TaskPicker
        userId={userId}
        value={taskId}
        error={issues.taskId ?? null}
        editing={
          entry
            ? { taskId: entry.taskId, taskTitle: entry.taskTitle, projectName: entry.projectName }
            : null
        }
        onChange={(next) => {
          setTaskId(next);
          clearIssue('taskId');
        }}
      />

      {/* Date and Time each on their own line, matching the pickers above. */}
      <div className="grid gap-4">
        <Field
          label="Date"
          type="date"
          required
          min={weekStart}
          max={weekEnd}
          value={workDate}
          error={issues.workDate ?? null}
          hint="Within the week you are viewing"
          onChange={(event) => {
            setWorkDate(event.target.value);
            clearIssue('workDate');
          }}
        />

        <FieldShell
          label="Time"
          required
          error={issues.minutes ?? null}
          hint={`Quarter-hour steps · ${formatHours(Number.isNaN(minutes) ? null : minutes)}`}
        >
          {({ id, describedBy, invalid }) => (
            <div className="flex items-stretch gap-2">
              <Button
                size="md"
                variant="secondary"
                aria-label="Fifteen minutes less"
                disabled={!Number.isNaN(minutes) && minutes <= MINUTES_MIN}
                onClick={() => step(-1)}
                className="w-10 shrink-0 px-0 font-mono text-base"
              >
                &minus;
              </Button>
              <input
                id={id}
                type="number"
                inputMode="numeric"
                step={MINUTES_STEP}
                min={MINUTES_MIN}
                max={MINUTES_MAX}
                required
                value={minutesText}
                aria-invalid={invalid}
                aria-describedby={describedBy}
                onChange={(event) => {
                  setMinutesText(event.target.value);
                  clearIssue('minutes');
                }}
                className={cn(
                  CONTROL_CLASS,
                  'h-10 min-w-0 flex-1 text-center font-mono tnum',
                  invalid ? 'border-danger' : 'border-line-strong hover:border-ink/25',
                )}
              />
              <Button
                size="md"
                variant="secondary"
                aria-label="Fifteen minutes more"
                disabled={!Number.isNaN(minutes) && minutes >= MINUTES_MAX}
                onClick={() => step(1)}
                className="w-10 shrink-0 px-0 font-mono text-base"
              >
                +
              </Button>
            </div>
          )}
        </FieldShell>
      </div>

      <Textarea
        label="Notes"
        rows={3}
        maxLength={5000}
        placeholder="What you worked on"
        hint="Optional, but it is what the review reads."
        value={notes}
        error={issues.notes ?? null}
        onChange={(event) => setNotes(event.target.value)}
      />

      <div className="flex flex-wrap items-center justify-end gap-2">
        {onCancel ? (
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" variant="primary" loading={busy} iconLeft={entry ? 'check' : 'plus'}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/**
 * Today, when today is in the week being viewed; otherwise its Monday.
 *
 * `todayInBusinessZone()` rather than `new Date().toISOString().slice(0, 10)`:
 * for seven hours every evening those two disagree, and the symptom is work
 * logged after five o'clock landing on tomorrow.
 */
function defaultWorkDate(weekStart: PlainDate): PlainDate {
  const today = todayInBusinessZone();
  return today >= weekStart && today <= addDays(weekStart, 6) ? today : weekStart;
}
